import { useState, useCallback, useEffect } from "react";

// region Imports - Shared / Utils
import api from "@services/api";
import { User } from "@shared/interfaces/user.interface";
import { ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Shared / Utils

interface UseUsersListProps {
  addToast: (props: any) => void;
  t: (key: string) => string;
}
export function useUsersList({ addToast, t }: UseUsersListProps) {

  // region States
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUserList, setLoadingUserList] = useState(false);
  const [table, setTable] = useState<DataTables.Api>({} as DataTables.Api);
  const [tableClickedRow, setTableClickedRow] = useState<JQuery<HTMLTableRowElement>>({} as JQuery<HTMLTableRowElement>);
  // endregion States
  // region Functions

  /**
   * Asynchronously fetches a list of users from the server and updates the application state with the fetched data.
   */
  const readUsers = useCallback(async () => {
    try {
      setLoadingUserList(true);
      const { data } = await api.get("users/read");

      if (data.status === "success") {
        setUsers(data.result);
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingUserList(false);
    }
  }, [addToast, t]);

  /**
   * Asynchronously deletes a user and handles the response.
   *
   * @param {User} user - The user object containing information about the user to be deleted. Must include `id_user`.
   * @param {Function} callbackOnSuccess - A callback function executed when the user deletion is successful.
   */
  const deleteUser = useCallback(async (user: User, callbackOnSuccess: () => void) => {
    try {
      setLoadingUserList(true);
      const { data } = await api.delete("users/delete/" + user.id_user);

      if (data.status === "success") {
        callbackOnSuccess();
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });

        if (table?.row && tableClickedRow.length) {
          table.row(tableClickedRow).remove();
          table.search("");
          table.draw();
        }
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingUserList(false);
    }
  }, [table, tableClickedRow, addToast, t]);

  // endregion Functions
  // region Effects

  // Initially fetch users
  useEffect(() => {
    const init = async () => { await readUsers(); };

    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // endregion Effects

  return { users, loadingUserList, table, setTable, tableClickedRow, setTableClickedRow, readUsers, deleteUser };
}
